'use client';
import { useEffect, useState } from 'react';
import Script from 'next/script';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/Button/Button';
import { Loader } from '@/components/Loader/Loader';
import { Modal, type ModalProps } from '@/components/Modal/Modal';
import { LoginModalContent } from './LoginModalContent';
import { useLoginModalStore } from '@/stores/login-modal-loaded-store';
import { env } from '@/utils/env/env';

type LoginModalProps = { onLoginSuccess?: () => void; countryCode: string } & Omit<ModalProps, 'children' | 'image' | 'variant'>;

export function LoginModal({ onLoginSuccess, countryCode, ...props }: LoginModalProps) {
	const { loginModalLoadFailure, loginModalLoading, setLoginModalLoadFailed, setModalLoaded } = useLoginModalStore((state) => state);
	const t = useTranslations('global.login-modal');

	const [isRetryLoading, setIsRetryLoading] = useState(false);

	useEffect(() => {
		const handleLoginModaLSetup = (event: CustomEvent) => {
			window.removeEventListener('loginModalSetup', handleLoginModaLSetup as EventListener);

			const success = event.detail?.success ?? false;

			if (success) {
				setModalLoaded();
			} else {
				setLoginModalLoadFailed();
			}
		};

		if (loginModalLoading) {
			window.addEventListener('loginModalSetup', handleLoginModaLSetup as EventListener);
		}

		return () => {
			window.removeEventListener('loginModalSetup', handleLoginModaLSetup as EventListener);
		};
	}, []);

	return (
		<>
			{/* TODO: These scripts load even if its not triggered to load, its a perf issue. [LoginModalPerf] */}
			<Script
				src={env(('PUBLIC_LOGIN_SCRIPT_' + countryCode + '_URL') as keyof typeof env)}
				onError={() => {
					setLoginModalLoadFailed();
				}}
				strategy="afterInteractive"
			></Script>
			<Script id="ssoinit">
				{`
				function waitFor(predicate, timeout) {
					return new Promise((resolve, reject) => {
						let running = true;

						const check = async () => {
							const result = await predicate();
							if (result) return resolve();
							if (running) setTimeout(check, 100);
						};

						check();

						if (timeout) {
							setTimeout(() => {
								running = false;
								reject(new Error('Timed out waiting for predicate'));
							}, timeout);
						}
					});
				}

				async function init() {
					const dxpSettingsResponse = await fetch('${env(('PUBLIC_LOGIN_SETTINGS_' + countryCode + '_URL') as keyof typeof env)}');

					if (!dxpSettingsResponse.ok) {
						throw new Error('Failed to fetch DXP settings: ' + dxpSettingsResponse.status);
					}

					window.dxp = window.T1 = await dxpSettingsResponse.json();
					window.dxp.settings.isNonDxpEnvironment = true;

					await waitFor(() => !!(window).requirees, 10000);

					window.requirees.register({
						'postal@2.0.5': 'https://cdnjs.cloudflare.com/ajax/libs/postal.js/2.0.5/postal.min.js',
						'lodash@4.17.20': 'https://cdnjs.cloudflare.com/ajax/libs/lodash.js/4.17.20/lodash.min.js'
					});

					window.postal = await require("postal");

					requirees(window.dxp.settings.ssoRedesignBaseUrl + '/configuration.js');
				}

				(async () => {
					try {
						await init();
						window.dispatchEvent(new CustomEvent('loginModalSetup', { detail: { success: true } }));

						// Subscribe to postal events for analytics tracking of the CP login component
						window.postal.subscribe({
							topic: 'onTrack',
							channel: 'com.toyota.tme.t1.statistics',
							callback: function (data) {
								if (data.extraData) {
									window.tngaDataLayer = {
										...window.tngaDataLayer,
										...data.extraData
									};

									window.dataLayer?.push({ event: data.extraData.event.name });
								}
							}
						});
					} catch (error) {
						console.error('Error initializing login modal:', error);
						window.dispatchEvent(new CustomEvent('loginModalSetup', { detail: { success: false } }));
					}
				})();
			`}
			</Script>
			{loginModalLoadFailure ? (
				<Modal
					size="sm"
					{...props}
					variant="compact"
					title={t('service-issue.title')}
					footerActions={
						<Button
							className="w-full"
							loading={isRetryLoading}
							onClick={() => {
								setIsRetryLoading(true);
								window.location.reload();
							}}
						>
							{t('service-issue.cta')}
						</Button>
					}
				>
					<p>{t('service-issue.description')}</p>
				</Modal>
			) : (
				<Modal
					size="lg"
					{...props}
					variant="compact"
					title={t('title')}
					hideTitle
					onInteractOutside={(event) => event.preventDefault()} // Prevent closing the modal on outside click (this is important because if you create a new account, the modal can close and you can lose the data)
					onEscapeKeyDown={(event) => event.preventDefault()} // Prevent closing the modal on escape key
				>
					{loginModalLoading ? (
						<div className="d-flex justify-center items-center">
							<Loader />
						</div>
					) : (
						<LoginModalContent onLoginSuccess={onLoginSuccess} onLoadFailed={setLoginModalLoadFailed} />
					)}
				</Modal>
			)}
		</>
	);
}
